import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useCallback,
  useEffect,
} from "react";
import { Col, Row } from "react-bootstrap";
import classes from "@/Modals/BaseModal.module.css";
import { useFormik } from "formik";
import ModalSkeleton from "@/components/ModalSkeleton";
import Button from "@/components/Button";
import { usePost } from "@/apiHooks/HttpFunctions";
import {
  gendersMasterDataApi,
  productGenderSizeMappingsApi,
  productSizesMasterDataApi,
  productsMasterDataApi,
} from "@/apis/apis";
import RenderToast from "@/components/RenderToast";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import ProductGenderSizeInitialValues from "@/data/WebDataManagement/Mapping/ProductGenderSize/initial-values";
import ProductGenderSizeSchema from "@/schema/WebDataManagement/Mapping/ProductGenderSize/ProductGenderSizeSchema";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import { LIST_MODE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";

const ProductGenderSizeModal = forwardRef((_, ref) => {
  const [saveError, setSaveError] = useState(null);
  const [modalState, setModalState] = useState({ isOpen: false });

  const handleSubmit = (values) => {
    const payload = {
      product_id: values.product_id,
      gender_id: values.gender_id,
      product_size_id: values.product_size_id,
    };
    saveAdd(JSON.stringify(payload));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ProductGenderSizeInitialValues(),
    validationSchema: ProductGenderSizeSchema(),
    onSubmit: handleSubmit,
  });

  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [formik, resetError]);

  const handleSuccess = useCallback(() => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Product - Gender - Size added successfully.`,
    });
  }, [closeModal]);

  const {
    mutate: saveAdd,
    isPending: saveIsPending,
    error: saveAddError,
  } = usePost({
    apiURL: `${productGenderSizeMappingsApi}`,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    setSaveError(saveAddError);
  }, [saveAddError]);

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const productListApi = `${productsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;
  const {
    loading: productLoading,
    defaultOptions: productDefaultOptions,
    debouncedFetchOptions: productFetchOptions,
    getSelectedOption: productGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productListApi,
    formikValue: formik.values.product_id,
    formikInitialValue: formik.initialValues.product_id,
  });

  const genderListApi = `${gendersMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;
  const {
    loading: genderLoading,
    defaultOptions: genderDefaultOptions,
    debouncedFetchOptions: genderFetchOptions,
    getSelectedOption: genderGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: genderListApi,
    formikValue: formik.values.gender_id,
    formikInitialValue: formik.initialValues.gender_id,
  });

  const productSizeListApi = `${productSizesMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;
  const {
    loading: productSizeLoading,
    defaultOptions: productSizeDefaultOptions,
    debouncedFetchOptions: productSizeFetchOptions,
    getSelectedOption: productSizeGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productSizeListApi,
    formikValue: formik.values.product_size_id,
    formikInitialValue: formik.initialValues.product_size_id,
  });

  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <ModalSkeleton
      header="Add Product - Gender - Size"
      onHide={closeModal}
      show={modalState.isOpen}
      modalStyles={classes.modal}
      showCloseIcon={true}
      showLogo={true}
    >
      <form
        onSubmit={formik.handleSubmit}
        className="h-[50vh] overflow-hidden flex gap-y-2 flex-col"
      >
        <div className="flex-1 flex pe-4 flex-col gap-y-2 overflow-y-auto">
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Product"
                placeholder="No product selected. Type to search..."
                defaultOptions={productDefaultOptions}
                loadOptions={productFetchOptions}
                isLoading={productLoading}
                value={productGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue("product_id", selected?.value || null);
                }}
                errorText={
                  (formik.touched.product_id && formik.errors.product_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.product_id?.join(" "))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Gender"
                placeholder="No gender selected. Type to search..."
                defaultOptions={genderDefaultOptions}
                loadOptions={genderFetchOptions}
                isLoading={genderLoading}
                value={genderGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue("gender_id", selected?.value || null);
                }}
                errorText={
                  (formik.touched.gender_id && formik.errors.gender_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.gender_id?.join(" "))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Size"
                placeholder="No size selected. Type to search..."
                defaultOptions={productSizeDefaultOptions}
                loadOptions={productSizeFetchOptions}
                isLoading={productSizeLoading}
                value={productSizeGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue(
                    "product_size_id",
                    selected?.value || null
                  );
                }}
                errorText={
                  (formik.touched.product_size_id &&
                    formik.errors.product_size_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.product_size_id?.join(
                      " "
                    ))
                }
              />
            </Col>
          </Row>
        </div>
        <div>
          <Row className="mt-4">
            <Col md={6}>
              <Button
                variant="outlined"
                label="Cancel"
                onClick={closeModal}
                type="button"
                className={classes.button}
                disabled={saveIsPending}
              />
            </Col>
            <Col md={6}>
              <Button
                id="submit"
                variant="green-gradient"
                label={saveIsPending ? LOADING_PLACEHOLDER : "Save"}
                type="submit"
                className={classes.button}
                disabled={saveIsPending}
              />
            </Col>
          </Row>
        </div>
      </form>
    </ModalSkeleton>
  );
});

export default ProductGenderSizeModal;
